import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Shift, TimelineShift } from '@wilson/interfaces';
import { AssignShiftHelperService } from '@wilson/non-domain-specific/shift-helpers/core';
import { CheckShiftActivityPermissionsService } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  ShiftId,
  ShiftTimelineDataTransformerService,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { cloneDeep } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  SelectedShift,
  ShiftDrawerService,
} from '../../../services/shift-drawer.service';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import { TimelineShiftAssignService } from '../../../services/timeline-shift-assign.service';
import { UnassignShiftCommonOperationsService } from '../../../services/unassign-shift-common-operations.service';
import { MultipleShiftsDrawerComponent } from '../../drawers/multiple-shifts-drawer/multiple-shifts-drawer.component';
import { TimelineDrawerComponent } from '../../drawers/timeline-drawer/timeline-drawer.component';
import { TimelineMultiSelectHelperService } from '../../timeline-multiselect/timeline-multi-select-helper.service';

@Component({
  selector: 'wilson-standard-view-drawers',
  template: `
    @let isDrawerVisible = (isDrawerVisible$ | async) ?? false;
    @let selectedShiftId = (selectedShiftId$ | async) ?? null;
    <wilson-simplified-or-detail-shift-wrapper
      [isDrawerVisible]="isDrawerVisible"
      [selectedShiftId]="selectedShiftId"
      (closeDrawer)="shiftDrawerService.closeDrawer()"
      (deletionConfirmed)="handleShiftDeletion($event)"
    >
      <ng-template>
        <wilson-timeline-drawer
          (deletionConfirmed)="handleShiftDeletion($event)"
          (reloadedUnassignedShift)="handleReloadUnassignedShift($event)"
          (closeDrawer)="shiftDrawerService.closeDrawer()"
        ></wilson-timeline-drawer>
      </ng-template>
    </wilson-simplified-or-detail-shift-wrapper>
    <wilson-multiple-shifts-drawer
      (deleteOperationTriggered)="handleMultiShiftDeletion($event)"
      (unassignShiftsOperationTriggered)="handleMultiShiftUnassign($event)"
      (moveShiftOperationTriggered)="handleMultiShiftMove($event)"
      (assignShiftOperationTriggered)="handleMultiShiftAssignOperation($event)"
    ></wilson-multiple-shifts-drawer>

    <wilson-timeline-activity-drawer></wilson-timeline-activity-drawer>
    <wilson-timeline-create-stays-drawer></wilson-timeline-create-stays-drawer>
    <wilson-timeline-stays-edit-drawer></wilson-timeline-stays-edit-drawer>
  `,
  styles: ``,
  providers: [
    TimelineShiftAssignService,
    AssignShiftHelperService,
    TimelineMultiSelectHelperService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StandardViewDrawersComponent {
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  protected readonly shiftDrawerService = inject(ShiftDrawerService);
  private timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private timelineMultiSelectHelperService = inject(
    TimelineMultiSelectHelperService,
  );
  private readonly unassignShiftCommonOperationsService = inject(
    UnassignShiftCommonOperationsService,
  );
  private readonly assignShiftHelperService = inject(AssignShiftHelperService);
  private readonly timelineShiftAssignService = inject(
    TimelineShiftAssignService,
  );
  protected shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  protected timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly checkShiftActivityPermissionsService = inject(
    CheckShiftActivityPermissionsService,
  );

  @ViewChild(MultipleShiftsDrawerComponent)
  multiShiftDrawer!: MultipleShiftsDrawerComponent;

  @ViewChild(TimelineDrawerComponent)
  timelineDrawer!: TimelineDrawerComponent;

  protected readonly isDrawerVisible$ =
    this.shiftDrawerService.isDrawerVisible$;
  protected readonly selectedShiftId$ =
    this.shiftDrawerService.selectedShiftId$;

  async handleMultiShiftUnassign(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
      const shiftsToUnassign: (Shift & { id: string })[] = [];
      const shiftsToUnassignIds: ShiftId[] = [];
      for (const shift of shifts) {
        if (shift.userId) {
          shiftsToUnassign.push(shift);
          shiftsToUnassignIds.push(shift.id);
        }
      }
      this.checkShiftActivityPermissionsService.checkShiftActivityPermissions({
        shiftIds: shiftsToUnassignIds,
        onSuccess: async () => {
          await this.unassignShiftCommonOperationsService.handleStandardViewUnassign(
            shiftsToUnassign,
          );
        },
      });

      this.multiShiftDrawer.refreshDrawer();
      this.timelineMultiSelectHelperService.refreshStaysDataForShiftUsers(
        shifts.map((shift) => shift.userId as string),
      );
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  async handleMultiShiftDeletion(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
      await this.timelineMultiSelectHelperService.handleMultiShiftStandardViewDeleteOperation(
        shifts,
      );
      this.multiShiftDrawer.closeDrawer();
      this.timelineMultiSelectHelperService.refreshStaysDataForShiftUsers(
        shifts.map((shift) => shift.userId as string),
      );
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  handleReloadUnassignedShift(shift: SelectedShift): void {
    this.shiftDrawerService.updateUnassignedShifts(shift);
  }

  async handleShiftDeletion(shift: Shift & { id: string }): Promise<void> {
    try {
      await this.shiftDrawerService.deleteShiftInRegularMode(shift);
      if (shift.userId) {
        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          shift.userId,
        ]);
      }
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  async handleMultiShiftMove(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
      await this.timelineMultiSelectHelperService.handleMultiShiftStandardViewMoveOperation(
        shifts,
      );
      this.multiShiftDrawer.refreshDrawer();
      this.timelineMultiSelectHelperService.refreshStaysDataForShiftUsers(
        shifts.map((shift) => shift.userId as string),
      );
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  async handleMultiShiftAssignOperation(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
    try {
      const selectedUserId = await this.assignShiftHelperService.assignShifts(
        cloneDeep(shifts),
      );
      if (selectedUserId) {
        await this.timelineShiftAssignService.handleStandardViewShiftsWithoutActivitiesAssignments(
          selectedUserId,
          shifts as TimelineShift[],
        );
        this.multiShiftDrawer.refreshDrawer();
        this.timelineMultiSelectHelperService.refreshStaysDataForShiftUsers(
          shifts.map((shift) => shift.userId as string),
        );
      }
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }
}
